import FastModal from 'components/FastModal';
import ElementPrefixId from 'constants/elementPrefixId';
import {FmlxCheckBox, FmlxIcon, FmlxRadioButton, FmlxTooltip} from 'fmlx-common-ui';
import {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {hideTypedModal, setTypedModalData} from 'stores/actions/typedModalAction';
import InputFile from 'components/InputFile/InputFile';
import {FastRobotErrorcode, FileType} from 'constants/dataEnum';
import {
  downloadBackupFile,
  restoreData,
  uploadRestoreData,
  resetBackupRestoreState,
} from 'stores/business/backupAndRestoreBusiness';
import {getModalData} from 'stores/selectors/typedModalSelectors';
import InputFileHint from 'components/InputFile/InputFileHint';
import style from './ModalBackupAndRestoreData.module.scss';

const RadioOption = {
  DOWNLOAD_BACKUP_DATA: 'DOWNLOAD_BACKUP_DATA',
  RESTORE_DATA: 'RESTORE_DATA',
};
const ListBackupData = ['User accounts', 'Protocols', 'Plates', 'Protocol Execution Reports', 'Config files'];

function ModalBackupAndRestoreData() {
  const dispatch = useDispatch();
  const {formatMessage} = useIntl();
  const [mode, setMode] = useState(RadioOption.DOWNLOAD_BACKUP_DATA);
  const modalData = useSelector(getModalData);
  const {
    errorNumber = 0,
    errorCode = '',
    versionsErrorDetail = {},
    fileName = '',
    isRestoringConfig = false,
  } = modalData;
  const [restoreFile, setRestoreFile] = useState(null);

  useEffect(() => {
    if (fileName) {
      setMode(RadioOption.RESTORE_DATA);
    }
  }, [fileName]);

  const handleClickProceed = () => {
    switch (mode) {
      case RadioOption.DOWNLOAD_BACKUP_DATA:
        dispatch(downloadBackupFile());
        break;
      case RadioOption.RESTORE_DATA:
        dispatch(restoreData(isRestoringConfig));
        break;
      default:
        break;
    }
  };

  const handleCloseModal = () => {
    dispatch(resetBackupRestoreState());
    dispatch(hideTypedModal(true));
  };

  const handleOnCheckedRestoreConfig = () => {
    dispatch(setTypedModalData({...modalData, isRestoringConfig: !isRestoringConfig}));
  };

  const handleFileChange = (file) => {
    if (file) {
      dispatch(setTypedModalData({...modalData, fileName: file.name}));
      dispatch(uploadRestoreData(file));
    }

    setRestoreFile(file);
  };

  const getUploadFileHint = () => {
    let message = formatMessage({id: 'modal.backupAndRestoreData.upload.textLabel.default'});
    const variant = errorCode ? InputFileHint.Variant.DANGER : '';

    if (!errorCode) return {message, variant};

    switch (errorCode) {
      case FastRobotErrorcode.MISMATCH_SOFTWARE_VERSION: {
        message = formatMessage(
          {id: 'modal.backupAndRestoreData.upload.textLabel.mismatchSoftwareVersion'},
          {
            currentSWVersion: versionsErrorDetail.currentVersion,
            downloadedSWVersion: versionsErrorDetail.restorePkgVersion,
          },
        );
        break;
      }
      case FastRobotErrorcode.MISMATCH_INSTRUMENT_SERIAL_NUMBER_VERSION: {
        message = formatMessage(
          {id: 'modal.backupAndRestoreData.upload.textLabel.mismatchInstrumentVersion'},
          {
            currentSNVersion: versionsErrorDetail.currentSerNum,
            downloadedSNVersion: versionsErrorDetail.restorePkgSerNum,
          },
        );
        break;
      }
      case FastRobotErrorcode.MISMATCH_SOFTWARE_AND_INSTRUMENT_VERSION: {
        message = formatMessage(
          {id: 'modal.backupAndRestoreData.upload.textLabel.mismatchSoftwareAndInstrumentVersion'},
          {
            currentSWVersion: versionsErrorDetail.currentVersion,
            downloadedSWVersion: versionsErrorDetail.restorePkgVersion,
            currentSNVersion: versionsErrorDetail.currentSerNum,
            downloadedSNVersion: versionsErrorDetail.restorePkgSerNum,
          },
        );
        break;
      }
      case FastRobotErrorcode.INVALID_RESTORE_PACKAGE:
      default: {
        message = formatMessage({id: 'modal.backupAndRestoreData.upload.textLabel.invalidFile'});
        break;
      }
    }

    return {message, variant};
  };

  const disableProceedButton = () =>
    (!fileName || (errorCode && errorCode !== FastRobotErrorcode.NoError)) && mode === RadioOption.RESTORE_DATA;

  const renderDownloadOptionContent = () => {
    const tooltipMessage = (
      <div>
        {formatMessage({id: 'modal.backupAndRestoreData.radioButton.tooltip.download'})}
        <ul className={style.list}>
          {ListBackupData.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    );

    return (
      <div className={style.radioLabel}>
        <span>{formatMessage({id: 'modal.backupAndRestoreData.radioButton.label.download'})}</span>
        <FmlxTooltip title={tooltipMessage}>
          <span>
            <FmlxIcon name="HelpCircle" size={FmlxIcon.Size.EXTRA_SMALL} />
          </span>
        </FmlxTooltip>
      </div>
    );
  };

  const renderUploadSubContent = () => (
    <div className={style.restore}>
      <div className={style.inputFile}>
        <InputFile
          id={`${ElementPrefixId.MODAL_BACKUP_AND_RESTORE_DATA}-input-file`}
          value={restoreFile}
          fileName={fileName ?? restoreFile?.name}
          fileTypes={FileType.ZIP}
          label="BROWSE"
          onChange={handleFileChange}
          hint={getUploadFileHint()}
        />
      </div>
      <div className={style.checkbox}>
        <FmlxCheckBox
          id={`${ElementPrefixId.MODAL_BACKUP_AND_RESTORE_DATA}-checkbox-restore-config`}
          label={<span>{formatMessage({id: 'modal.backupAndRestoreData.checkbox.label.restoreConfigFiles'})}</span>}
          checked={isRestoringConfig}
          onChange={handleOnCheckedRestoreConfig}
        />
      </div>
    </div>
  );

  const content = (
    <div id={`${ElementPrefixId.MODAL_BACKUP_AND_RESTORE_DATA}-container`} className={style.container}>
      <span className={style.selectOptionLabel}>{formatMessage({id: 'modal.backupAndRestoreData.text'})}</span>
      <FmlxRadioButton
        id={`${ElementPrefixId.MODAL_BACKUP_AND_RESTORE_DATA}-radio-button`}
        selectedIndex={Object.values(RadioOption).indexOf(mode)}
        items={[
          {content: renderDownloadOptionContent()},
          {
            content: formatMessage({id: 'modal.backupAndRestoreData.radioButton.label.restore'}),
            showSubContent: mode === RadioOption.RESTORE_DATA,
            subContent: renderUploadSubContent(),
          },
        ]}
        direction={FmlxRadioButton.Direction.COLUMN}
        onChange={({index}) => setMode(Object.values(RadioOption)[index])}
      />
    </div>
  );

  const buttons = [
    {
      className: 'btn-lg',
      style: 'primary',
      text: formatMessage({id: 'modal.backupAndRestoreData.button.proceed'}),
      handlerClick: handleClickProceed,
      disabled: disableProceedButton(),
    },
    {
      className: 'btn-lg',
      style: 'secondary',
      text: formatMessage({id: 'modal.backupAndRestoreData.button.cancel'}),
      handlerClick: handleCloseModal,
    },
  ];

  return (
    <FastModal
      show
      header={formatMessage({id: 'modal.backupAndRestoreData.title'})}
      content={content}
      errorCodeNumber={errorNumber}
      btnAction={buttons}
      onClose={handleCloseModal}
      closeDisabled={false}
    />
  );
}

export default ModalBackupAndRestoreData;
